import { api } from "@/api/client";

export interface ZohoIntegration {
  id: string;
  org_id?: string;
  zoho_org_id: string | null;
  zoho_org_name?: string | null;
  integration_type?: string | null;
  data_center?: string;
  dc?: string;
  connection_status: string;
  connected_at?: string | null;
  last_sync_at: string | null;
}

export type ZohoSyncModuleStatus = "ok" | "error";
export type ZohoSyncModuleName =
  | "contacts"
  | "items"
  | "invoices"
  | "sales_orders"
  | "purchase_orders";

export interface ZohoSyncModule {
  module: ZohoSyncModuleName;
  last_sync_at: string | null;
  record_count: number;
  status: ZohoSyncModuleStatus;
  error_message?: string | null;
}

export interface ZohoSyncStatus {
  modules: ZohoSyncModule[];
  polled_every_minutes: number;
}

export interface EmailProviderStatus {
  connected: boolean;
  healthy: boolean;
  last_synced_at: string | null;
  days_stale: number | null;
}

export const EMPTY_EMAIL_PROVIDER: EmailProviderStatus = {
  connected: false,
  healthy: false,
  last_synced_at: null,
  days_stale: null,
};

export interface EmailIntegrationStatus {
  google: EmailProviderStatus;
  microsoft: EmailProviderStatus;
}

export type ZohoRole = "admin" | "staff" | "accountant" | (string & {});

export interface ZohoPermissions {
  zoho_role: ZohoRole | null;
  zoho_email: string | null;
  personal_token_connected: boolean;
  data_center?: string;
  can_view: string[];
  can_write: string[];
}

// Default used while the permissions request is in flight, or when it fails —
// in both cases we surface the most restrictive state.
export const EMPTY_ZOHO_PERMISSIONS: ZohoPermissions = {
  zoho_role: null,
  zoho_email: null,
  personal_token_connected: false,
  can_view: [],
  can_write: [],
};

export type ZohoTokenType = "org" | "personal" | "analytics";

export type EmailStartResponse =
  | { auth_url: string; already_connected?: false }
  | { already_connected: true; auth_url?: undefined };

export const integrationsApi = {
  // Start Zoho OAuth — returns auth_url to redirect user to.
  // Always uses personal token_type; the backend auto-bootstraps the org
  // integration for admins so no separate org OAuth step is needed.
  startZoho: (
    dataCenter: string = "in",
    tokenType: ZohoTokenType = "personal",
  ): Promise<{ auth_url: string; state: string }> =>
    api.post("/v1/zoho/oauth/start", { data_center: dataCenter, token_type: tokenType }),

  // List connected Zoho integrations for this org. Backend may return either
  // a bare array or `{ integrations: [...] }`; we normalize to a bare array.
  listZohoIntegrations: async (): Promise<ZohoIntegration[]> => {
    const res = await api.get<ZohoIntegration[] | { integrations: ZohoIntegration[] }>(
      "/v1/zoho/integrations",
    );
    if (Array.isArray(res)) return res;
    return res?.integrations ?? [];
  },

  getZohoSyncStatus: (integrationId: string): Promise<ZohoSyncStatus> =>
    api.get(`/v1/zoho/sync-status?integration_id=${encodeURIComponent(integrationId)}`),

  // Fetch the current user's Zoho permissions.
  // Pass integrationId to get org-scoped can_view / can_write; omit for personal-token status only.
  getMyZohoPermissions: (integrationId?: string): Promise<ZohoPermissions> =>
    api.get(
      integrationId
        ? `/v1/zoho/my-permissions?integration_id=${encodeURIComponent(integrationId)}`
        : "/v1/zoho/my-permissions",
    ),

  getPersonalZohoPermissions: (): Promise<ZohoPermissions> =>
    api.get("/v1/zoho/my-permissions"),

  // Null out the current user's personal Zoho token without deleting the row.
  disconnectPersonalZoho: (): Promise<{ status: string }> =>
    api.delete("/v1/zoho/users/me/zoho-connection"),

  triggerZohoSync: (integrationId: string): Promise<{ ok: boolean }> =>
    api.post("/v1/zoho/sync/trigger", { integration_id: integrationId }),

  // Disconnect a Zoho integration
  disconnectZoho: (integrationId: string): Promise<ZohoIntegration> =>
    api.delete(`/v1/zoho/integrations/${integrationId}`),

  // Pause syncing without disconnecting
  pauseZoho: (integrationId: string): Promise<ZohoIntegration> =>
    api.post(`/v1/zoho/integrations/${integrationId}/pause`, {}),

  // Resume syncing
  resumeZoho: (integrationId: string): Promise<ZohoIntegration> =>
    api.post(`/v1/zoho/integrations/${integrationId}/resume`, {}),

  getEmailStatus: (): Promise<{ data: EmailIntegrationStatus }> =>
    api.get("/v1/integrations/email/status"),

  startGoogleEmail: (redirectUri?: string, force?: boolean): Promise<{ data: EmailStartResponse }> => {
    const params = new URLSearchParams();
    if (redirectUri) params.set("redirect_uri", redirectUri);
    if (force) params.set("force", "true");
    const qs = params.toString();
    return api.post(`/v1/integrations/google/email/start${qs ? `?${qs}` : ""}`, {});
  },

  startMicrosoftEmail: (redirectUri?: string, force?: boolean): Promise<{ data: EmailStartResponse }> => {
    const params = new URLSearchParams();
    if (redirectUri) params.set("redirect_uri", redirectUri);
    if (force) params.set("force", "true");
    const qs = params.toString();
    return api.post(`/v1/integrations/microsoft/email/start${qs ? `?${qs}` : ""}`, {});
  },

  disconnectGoogleEmail: (): Promise<{ ok: boolean }> =>
    api.delete("/v1/integrations/google/email"),

  disconnectMicrosoftEmail: (): Promise<{ ok: boolean }> =>
    api.delete("/v1/integrations/microsoft/email"),

  getAnalyticsMe: (): Promise<{ connected: boolean; zoho_email: string | null; analytics_role: string | null }> =>
    api.get("/v1/zoho/analytics/me"),
};
